import { useEffect, useState } from 'react';
import {
  Badge, Box, Button, Divider, Menu, MenuItem, Popover, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { ExpandMore, Tune } from '@mui/icons-material';
import { brand, text } from '../../theme/colors';
import { INK } from './SearchHeader';

export const TYPE_OPTIONS = [
  { value: '', label: 'All homes' },
  { value: 'house', label: 'Houses' },
  { value: 'stand', label: 'Stands' },
];
export const BED_CHOICES = ['', '1', '2', '3', '4', '5'];
export const BATH_CHOICES = ['', '1', '1.5', '2', '3'];

/** Comma list in the URL ↔ array. */
export const featureList = (v) => String(v || '').split(',').map((f) => f.trim()).filter(Boolean);

/**
 * Quick filters: realtor.com's row of one-tap chips, with Zimbabwean ones. Each
 * knows whether it is on for the current URL and what patch turns it on or off.
 */
export const QUICK_FILTERS = [
  {
    key: 'new', label: 'New in last 14 days',
    isOn: (v) => v.listed_within === '14',
    patch: (on) => ({ listed_within: on ? '' : '14' }),
  },
  {
    key: 'serviced', label: 'Serviced stands',
    isOn: (v) => v.type === 'stand' && featureList(v.features).includes('serviced'),
    patch: (on, v) => {
      const f = featureList(v.features).filter((x) => x !== 'serviced');
      return on ? { features: f.join(',') } : { type: 'stand', features: [...f, 'serviced'].join(',') };
    },
  },
  ...[['borehole', 'Borehole'], ['solar', 'Solar backup'], ['walled', 'Walled']].map(([feature, label]) => ({
    key: feature, label,
    isOn: (v) => featureList(v.features).includes(feature),
    patch: (on, v) => {
      const f = featureList(v.features).filter((x) => x !== feature);
      return { features: (on ? f : [...f, feature]).join(',') };
    },
  })),
  {
    key: 'title_deeds', label: 'Title deeds only',
    isOn: (v) => v.tenure === 'title_deed',
    patch: (on) => ({ tenure: on ? '' : 'title_deed' }),
  },
  {
    key: 'hide_sold', label: 'Hide sold',
    isOn: (v) => v.hide_sold === '1',
    patch: (on) => ({ hide_sold: on ? '' : '1' }),
  },
];

const boxSx = (active) => ({
  height: 46, px: 2, borderRadius: '10px', textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0,
  fontSize: 15, fontWeight: active ? 700 : 600, color: active ? '#134e4a' : INK,
  border: active ? `1.5px solid ${brand.primaryDark}` : '1px solid #b8c4c2',
  bgcolor: active ? '#e6f4f2' : '#fff',
  '&:hover': { bgcolor: active ? '#dcefec' : '#f6f9f8', borderColor: active ? brand.primaryDark : '#94a3b8' },
});

const money = (v) => `US$ ${Number(v).toLocaleString('en-US')}`;

function PricePill({ values, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const [min, setMin] = useState(values.min_price || '');
  const [max, setMax] = useState(values.max_price || '');
  useEffect(() => { setMin(values.min_price || ''); setMax(values.max_price || ''); }, [values.min_price, values.max_price]);
  const active = !!(values.min_price || values.max_price);
  const label = !active ? 'Price'
    : values.min_price && values.max_price ? `${money(values.min_price)} – ${money(values.max_price)}`
      : values.max_price ? `Up to ${money(values.max_price)}` : `From ${money(values.min_price)}`;
  // Both bounds leave in one patch: two calls would each start from the old URL.
  const apply = () => { setAnchor(null); onChange({ min_price: String(min).trim(), max_price: String(max).trim() }); };
  return (
    <>
      <Button endIcon={<ExpandMore />} onClick={(e) => setAnchor(e.currentTarget)} sx={boxSx(active)}>{label}</Button>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Stack spacing={1.5} sx={{ p: 2, width: 300 }}>
          <Typography sx={{ fontWeight: 700 }}>Price (US$)</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField size="small" type="number" label="Minimum" value={min} inputProps={{ min: 0 }} onChange={(e) => setMin(e.target.value)} />
            <Typography sx={{ color: text.secondary }}>to</Typography>
            <TextField size="small" type="number" label="Maximum" value={max} inputProps={{ min: 0 }} onChange={(e) => setMax(e.target.value)} />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={() => { setAnchor(null); onChange({ min_price: '', max_price: '' }); }} sx={{ textTransform: 'none' }}>Clear</Button>
            <Button size="small" variant="contained" onClick={apply} sx={{ textTransform: 'none', bgcolor: brand.primaryDark }}>Apply</Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}

/** A segmented "Any / 1+ / 2+ …" control, used by the pill and the Filters panel. */
export function CountChoice({ label, choices, value, onChange }) {
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>{label}</Typography>
      <ToggleButtonGroup
        exclusive fullWidth value={value || ''} onChange={(_, v) => v !== null && onChange(v)} aria-label={label}
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 15, py: 1, color: INK },
          '& .Mui-selected': { bgcolor: `${INK} !important`, color: '#fff !important', fontWeight: 700 } }}
      >
        {choices.map((c) => <ToggleButton key={c || 'any'} value={c}>{c ? `${c}+` : 'Any'}</ToggleButton>)}
      </ToggleButtonGroup>
    </Box>
  );
}

function BedsBathsPill({ values, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const active = !!(values.beds_min || values.baths_min);
  const label = !active ? 'Beds & baths'
    : [values.beds_min && `${values.beds_min}+ bd`, values.baths_min && `${values.baths_min}+ ba`].filter(Boolean).join(', ');
  return (
    <>
      <Button endIcon={<ExpandMore />} onClick={(e) => setAnchor(e.currentTarget)} sx={boxSx(active)}>{label}</Button>
      <Popover open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Stack spacing={2.5} sx={{ p: 2.5, width: 360 }}>
          <CountChoice label="Bedrooms" choices={BED_CHOICES} value={values.beds_min} onChange={(v) => onChange('beds_min', v)} />
          <CountChoice label="Bathrooms" choices={BATH_CHOICES} value={values.baths_min} onChange={(v) => onChange('baths_min', v)} />
          <Button onClick={() => setAnchor(null)} variant="contained" sx={{ textTransform: 'none', bgcolor: brand.primaryDark, alignSelf: 'flex-end' }}>Done</Button>
        </Stack>
      </Popover>
    </>
  );
}

function HomeTypePill({ values, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const current = TYPE_OPTIONS.find((o) => o.value === (values.type || '')) || TYPE_OPTIONS[0];
  const active = !!values.type;
  return (
    <>
      <Button endIcon={<ExpandMore />} onClick={(e) => setAnchor(e.currentTarget)} sx={boxSx(active)}>
        {active ? current.label : 'Home type'}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {TYPE_OPTIONS.map((o) => (
          <MenuItem key={o.value || 'all'} selected={o.value === (values.type || '')} onClick={() => { setAnchor(null); onChange('type', o.value); }}>
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * The second row: Filters (the full panel), then Price, Beds & baths and Home
 * type, then the quick filters. On a phone the row scrolls sideways inside its
 * own box — the page itself never does.
 */
export default function FilterRow({ values = {}, onChange, onOpenFilters, activeCount = 0 }) {
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, py: 0.5,
        flexWrap: { xs: 'nowrap', md: 'wrap' }, overflowX: { xs: 'auto', md: 'visible' }, minWidth: 0,
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Button onClick={onOpenFilters} startIcon={<Tune />} sx={boxSx(false)}>
        Filters
        {activeCount > 0 && (
          <Badge badgeContent={activeCount} sx={{ ml: 2, mr: 0.75, '& .MuiBadge-badge': { bgcolor: brand.primaryDark, color: '#fff', fontWeight: 700 } }} />
        )}
      </Button>
      <PricePill values={values} onChange={onChange} />
      <BedsBathsPill values={values} onChange={onChange} />
      <HomeTypePill values={values} onChange={onChange} />
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
      {QUICK_FILTERS.map((q) => {
        const on = q.isOn(values);
        return (
          <Button key={q.key} aria-pressed={on} onClick={() => onChange(q.patch(on, values))} sx={boxSx(on)}>
            {q.label}
          </Button>
        );
      })}
    </Box>
  );
}
